import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, CheckCircle2, RefreshCw, Play, Eye, UserPlus, AlertTriangle, RotateCcw, ChevronDown, ChevronRight, List, Users, Layers, Sparkles, Gauge } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const statusColor = {
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  imported: "bg-blue-100 text-blue-700 border-blue-200",
  error: "bg-red-100 text-red-700 border-red-200",
  superseded: "bg-amber-100 text-amber-800 border-amber-200",
};

const confidenceColor = (c) => {
  if (c >= 90) return "bg-emerald-50 text-emerald-700";
  if (c >= 70) return "bg-amber-50 text-amber-700";
  return "bg-red-50 text-red-700";
};

export default function ImportBatchPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  useDocumentTitle("Review Import", "Verify extracted rows before committing.");

  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [matchFilter, setMatchFilter] = useState("all");
  const [confidenceFilter, setConfidenceFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [members, setMembers] = useState([]);
  const [quals, setQuals] = useState([]);
  const [previewId, setPreviewId] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("importsViewMode") || "table");
  const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem("importsPageSize")) || 50);
  const [page, setPage] = useState(1);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [commitPreviewOpen, setCommitPreviewOpen] = useState(false);
  const [previewNameEdits, setPreviewNameEdits] = useState({}); // rowId -> new name
  const [savingPreviewEdits, setSavingPreviewEdits] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => { localStorage.setItem("importsViewMode", viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem("importsPageSize", String(pageSize)); }, [pageSize]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/import/v2/batches/${id}?limit=1000`);
      setBatch(r.data.batch);
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load batch");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    Promise.all([
      api.get("/members?role=student&limit=2000").then((r) => setMembers(r.data.members || [])).catch(() => {}),
      api.get("/qualifications").then((r) => setQuals(r.data || [])).catch(() => {}),
    ]);
    pollRef.current = setInterval(() => {
      setBatch((prev) => {
        if (prev && (prev.status === "extracting" || prev.status === "committing")) {
          load();
        }
        return prev;
      });
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [id, load]);

  // Categorise each row into a "match" bucket — independent of the
  // status lifecycle, so admins can spot data-quality issues at a glance.
  //   matched: existing learner AND a qual was mapped
  //   new_learner: filename learner not in DB → we'll auto-create at commit
  //   missing_qual: learner OK (existing or new) but no qual code matched
  //   error: extraction couldn't parse anything usable (status = error)
  const matchBucket = (r) => {
    if (r.status === "error") return "error";
    const hasQual = !!r.matched_qual_id;
    if (!hasQual) return "missing_qual";
    if (r.matched_member_id) return "matched";
    if (r.will_create_learner) return "new_learner";
    return "matched";                     // fallback — treat as matched
  };

  // Confidence bucket — the vision LLM emits a 0-100 self-reported
  // score, capturing how sure it is about the extracted dates/cert#.
  // High rows are safe to bulk-approve; low rows should be eyeballed.
  //   high:   ≥ 90
  //   medium: 70 - 89
  //   low:    < 70
  const confBucket = (r) => {
    const c = Number(r.confidence || 0);
    if (c >= 90) return "high";
    if (c >= 70) return "medium";
    return "low";
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (matchFilter !== "all" && matchBucket(r) !== matchFilter) return false;
      if (confidenceFilter !== "all" && confBucket(r) !== confidenceFilter) return false;
      if (s && !`${r.filename} ${r.extracted?.learner_name || ""} ${r.matched_qual_code || ""}`.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [rows, statusFilter, matchFilter, confidenceFilter, search]);

  // Reset to page 1 whenever the filter set changes
  useEffect(() => { setPage(1); }, [statusFilter, matchFilter, confidenceFilter, search, viewMode]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // Group rows by learner for the "grouped" view. Uses the extracted learner
  // name if there's no matched member yet, so unmatched rows still cluster.
  const groups = useMemo(() => {
    if (viewMode !== "grouped") return [];
    const m = new Map();
    for (const r of filtered) {
      const key = r.matched_member_name || r.extracted?.learner_name || "— unparseable —";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return Array.from(m.entries())
      .map(([name, rs]) => ({ name, rows: rs }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered, viewMode]);

  const pagedGroups = useMemo(() => {
    if (viewMode !== "grouped") return [];
    const start = (page - 1) * pageSize;
    return groups.slice(start, start + pageSize);
  }, [groups, page, pageSize, viewMode]);
  const groupPageCount = Math.max(1, Math.ceil(groups.length / pageSize));

  const stats = useMemo(() => {
    const s = { pending: 0, approved: 0, rejected: 0, imported: 0, error: 0, superseded: 0 };
    rows.forEach((r) => { if (s[r.status] !== undefined) s[r.status]++; });
    return s;
  }, [rows]);

  // Per-row match categorisation counts (data-quality lens).
  const matchStats = useMemo(() => {
    const m = { matched: 0, new_learner: 0, missing_qual: 0, error: 0 };
    rows.forEach((r) => { m[matchBucket(r)] = (m[matchBucket(r)] || 0) + 1; });
    return m;
  }, [rows]);

  // Confidence bucket counts — high / medium / low. Superseded rows are
  // excluded because we don't want them to inflate the "needs review"
  // count when they'll be skipped at commit anyway.
  const confidenceStats = useMemo(() => {
    const c = { high: 0, medium: 0, low: 0 };
    rows.forEach((r) => {
      if (r.status === "superseded") return;
      c[confBucket(r)]++;
    });
    return c;
  }, [rows]);

  // Rows eligible for the "Approve high-confidence" one-click action:
  // pending + matched (existing learner OR will auto-create) + qual
  // mapped + confidence ≥ 90 + not an error/superseded row.
  const highConfidenceReadyIds = useMemo(() => rows
    .filter((r) => r.status === "pending"
                   && confBucket(r) === "high"
                   && r.matched_qual_id
                   && (r.matched_member_id || r.will_create_learner)
                   && r.extracted?.issue_date)
    .map((r) => r.id), [rows]);

  // Learner-level roll-up — counts one PERSON regardless of how many
  // certificate rows they have in the batch. "Ready" = at least one row
  // for that learner is fully in a healthy state (matched qual + no
  // error + not superseded) and none of their rows is stuck as error.
  const learnerStats = useMemo(() => {
    const existing = new Map();       // matched_member_id -> { total, ready }
    const brandNew = new Map();       // normalised learner name -> { total, ready }
    const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();

    for (const r of rows) {
      if (r.status === "error") continue;             // unparseable — can't attribute
      const healthy = (r.status === "pending" || r.status === "approved" || r.status === "imported")
                       && !!r.matched_qual_id;
      if (r.matched_member_id) {
        const cur = existing.get(r.matched_member_id) || { total: 0, ready: 0, name: r.matched_member_name };
        cur.total++;
        if (healthy) cur.ready++;
        existing.set(r.matched_member_id, cur);
      } else if (r.will_create_learner) {
        const k = norm(r.extracted?.learner_name);
        if (!k) continue;
        const cur = brandNew.get(k) || { total: 0, ready: 0, name: r.extracted?.learner_name };
        cur.total++;
        if (healthy) cur.ready++;
        brandNew.set(k, cur);
      }
    }

    // "Fully ready" = the learner has ≥1 healthy row AND ZERO of their
    // rows is in an error/rejected state. We already skipped error above
    // when counting — recount errors per learner to enforce the zero-rule.
    const errorsByExistingId = new Map();
    const errorsByNewName = new Map();
    for (const r of rows) {
      if (r.status !== "error" && r.status !== "rejected") continue;
      if (r.matched_member_id) {
        errorsByExistingId.set(r.matched_member_id, (errorsByExistingId.get(r.matched_member_id) || 0) + 1);
      } else if (r.will_create_learner) {
        const k = norm(r.extracted?.learner_name);
        if (k) errorsByNewName.set(k, (errorsByNewName.get(k) || 0) + 1);
      }
    }

    let existingReady = 0;
    for (const [id, v] of existing) {
      if (v.ready > 0 && !errorsByExistingId.get(id)) existingReady++;
    }
    let newReady = 0;
    for (const [k, v] of brandNew) {
      if (v.ready > 0 && !errorsByNewName.get(k)) newReady++;
    }

    return {
      existingCount: existing.size,
      existingReady,
      newCount: brandNew.size,
      newReady,
    };
  }, [rows]);

  const patchRow = async (rowId, patch, silent = false) => {
    try {
      const r = await api.patch(`/import/v2/rows/${rowId}`, patch);
      setRows((prev) => prev.map((x) => x.id === rowId ? { ...x, ...r.data } : x));
      if (!silent) toast.success("Updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const bulkStatus = async (status) => {
    if (selected.size === 0) return toast.error("Select at least one row");
    try {
      await api.post(`/import/v2/batches/${id}/bulk-status`, {
        row_ids: Array.from(selected),
        status,
      });
      toast.success(`${selected.size} row(s) marked ${status}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk update failed");
    }
  };

  const approveAllMatched = () => {
    const ids = rows
      .filter((r) => r.status === "pending" && r.matched_qual_id && r.extracted?.learner_name)
      .map((r) => r.id);
    if (ids.length === 0) return toast.error("No matched rows to approve");
    setSelected(new Set(ids));
    setTimeout(() => bulkStatus("approved"), 100);
  };

  // Approve every high-confidence + fully-matched row in one click.
  // Cheap safety layer: doesn't touch anything already imported/rejected
  // or that's missing a qualification; only pending rows the vision LLM
  // was ≥ 90% confident about.
  const approveHighConfidence = () => {
    if (highConfidenceReadyIds.length === 0) {
      return toast.error("No high-confidence rows ready to approve");
    }
    if (!confirm(
      `Approve ${highConfidenceReadyIds.length} high-confidence row(s) ` +
      `(confidence ≥ 90, matched + qual, dates present)?`
    )) return;
    setSelected(new Set(highConfidenceReadyIds));
    setTimeout(() => bulkStatus("approved"), 100);
  };

  // Rows that will auto-create a new learner at commit time. These are
  // what we show in the pre-commit preview modal so typos get caught.
  const newLearnerPreviewRows = useMemo(() => rows.filter(
    (r) => (r.status === "pending" || r.status === "approved")
           && r.will_create_learner
           && !r.matched_member_id
           && r.extracted?.learner_name
  ), [rows]);

  const openCommitFlow = () => {
    const readyCount = rows.filter(
      (r) => r.status === "pending" || r.status === "approved"
    ).length;
    if (readyCount === 0) return toast.error("Nothing to import");
    setPreviewNameEdits({});
    setCommitPreviewOpen(true);
  };

  const doCommit = async () => {
    // Persist any edited new-learner names first (via existing PATCH row).
    const edits = Object.entries(previewNameEdits).filter(
      ([, v]) => typeof v === "string" && v.trim().length > 0
    );
    if (edits.length > 0) {
      setSavingPreviewEdits(true);
      try {
        await Promise.all(edits.map(([rowId, name]) =>
          api.patch(`/import/v2/rows/${rowId}`, { learner_name: name.trim() })
        ));
      } catch (e) {
        setSavingPreviewEdits(false);
        return toast.error(formatApiError(e.response?.data?.detail) || "Failed to save edited names");
      }
      setSavingPreviewEdits(false);
    }
    setCommitPreviewOpen(false);
    await commit();
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const r = await api.post(`/import/v2/batches/${id}/commit`);
      setCommitResult(r.data);
      toast.success(`Created ${r.data.created_certs} certs + ${r.data.created_learners} learners`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const retry = async () => {
    const errCount = rows.filter((r) => r.status === "error").length;
    const msg = errCount > 0
      ? `Retry ${errCount} error row(s) and re-run extraction on any missing PDFs?`
      : `Re-run extraction on any missing PDFs?`;
    if (!confirm(msg)) return;
    try {
      const r = await api.post(`/import/v2/batches/${id}/retry`);
      toast.success(`Retrying — wiped ${r.data.deleted_error_rows} error row(s)`);
      setTimeout(load, 1000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Retry failed");
    }
  };

  const rerunDedup = async () => {
    if (!confirm(
      "Re-run duplicate detection on this batch?\n\n" +
      "Rows previously marked 'superseded' will be reset to 'pending' and " +
      "re-evaluated against the current dedup rules (same cert number, " +
      "same issue+expiry dates, or overlapping achievement codes) plus " +
      "certificates already in the database."
    )) return;
    try {
      const r = await api.post(`/import/v2/batches/${id}/rerun-dedup`);
      toast.success(
        `Dedup complete — ${r.data.in_batch_superseded} in-batch dupes, ` +
        `${r.data.already_in_db_superseded} already in DB`
      );
      setTimeout(load, 300);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Re-run dedup failed");
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  if (!batch) return <div className="p-8 text-center text-slate-500">Batch not found</div>;

  const readyToCommit = batch.status === "ready" || batch.status === "done";
  const progressPct = batch.total > 0 ? Math.round((batch.extracted / batch.total) * 100) : 0;

  // Row renderer used by BOTH the flat table and the grouped view. When
  // `inGroup` is true we hide the filename column since the parent group
  // header already shows the learner (and the filename may repeat across
  // multiple rows for the same PDF in the multi-qual fan-out case).
  const renderRow = (r, inGroup) => {
    const isSel = selected.has(r.id);
    const isImportedOrErr = r.status === "imported" || r.status === "superseded";
    return (
      <tr key={r.id} className={`border-b ${isSel ? "bg-blue-50/50" : "hover:bg-slate-50"}`} data-testid={`row-${r.id}`}>
        <td className="px-2 py-2 w-8">
          <Checkbox
            checked={isSel}
            disabled={isImportedOrErr}
            onCheckedChange={(v) => {
              setSelected((prev) => {
                const n = new Set(prev);
                if (v) n.add(r.id); else n.delete(r.id);
                return n;
              });
            }}
          />
        </td>
        <td className="px-3 py-2 max-w-xs">
          <div className="truncate text-xs" title={r.filename}>{r.filename}</div>
          <div className={`inline-block text-xs px-1.5 py-0.5 rounded ${confidenceColor(r.confidence)}`}>
            {r.confidence}%
          </div>
        </td>
        {!inGroup && (
          <td className="px-3 py-2 min-w-[180px]">
            {r.matched_member_id ? (
              <div className="font-medium">{r.matched_member_name}</div>
            ) : r.will_create_learner && r.extracted?.learner_name ? (
              <div className="text-blue-700 font-medium flex items-center gap-1">
                <UserPlus className="w-3.5 h-3.5" />
                {r.extracted.learner_name} <span className="text-xs font-normal text-blue-600">(will create)</span>
              </div>
            ) : (
              <div className="text-red-600">{r.extracted?.learner_name || "—"}</div>
            )}
            <Select
              value={r.matched_member_id || ""}
              onValueChange={(v) => patchRow(r.id, { matched_member_id: v || null })}
              disabled={isImportedOrErr}
            >
              <SelectTrigger className="h-7 text-xs mt-1"><SelectValue placeholder="Pick existing learner…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Use extracted name —</SelectItem>
                {members.slice(0, 500).map((m) => (
                  <SelectItem key={m.id || m._id} value={m.id || m._id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </td>
        )}
        <td className="px-3 py-2 min-w-[180px]">
          {r.matched_qual_code ? (
            <div className="font-medium">{r.matched_qual_code}</div>
          ) : (
            <div className="text-red-600 text-xs">
              <AlertTriangle className="w-3 h-3 inline mr-0.5" />
              unknown &quot;{r.extracted?.qual_code_raw || "—"}&quot;
            </div>
          )}
          <Select
            value={r.matched_qual_id || ""}
            onValueChange={(v) => patchRow(r.id, { matched_qual_id: v || null })}
            disabled={isImportedOrErr}
          >
            <SelectTrigger className="h-7 text-xs mt-1"><SelectValue placeholder="Pick qual…" /></SelectTrigger>
            <SelectContent>
              {quals.map((q) => (
                <SelectItem key={q.id || q._id} value={q.id || q._id}>{q.code} · {q.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
        <td className="px-3 py-2">
          <Input type="date" className="h-7 text-xs w-32"
            value={r.extracted?.issue_date || ""}
            disabled={isImportedOrErr}
            onBlur={(e) => {
              const v = e.target.value;
              if (v !== (r.extracted?.issue_date || "")) patchRow(r.id, { issue_date: v }, true);
            }}
            onChange={(e) => {
              setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, extracted: { ...x.extracted, issue_date: e.target.value } } : x));
            }}
          />
        </td>
        <td className="px-3 py-2">
          <Input type="date" className="h-7 text-xs w-32"
            value={r.extracted?.expiry_date || ""}
            disabled={isImportedOrErr}
            onBlur={(e) => {
              const v = e.target.value;
              if (v !== (r.extracted?.expiry_date || "")) patchRow(r.id, { expiry_date: v }, true);
            }}
            onChange={(e) => {
              setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, extracted: { ...x.extracted, expiry_date: e.target.value } } : x));
            }}
          />
        </td>
        <td className="px-3 py-2">
          <Input className="h-7 text-xs w-28"
            value={r.extracted?.cert_number || ""}
            disabled={isImportedOrErr}
            onBlur={(e) => {
              const v = e.target.value;
              if (v !== (r.extracted?.cert_number || "")) patchRow(r.id, { cert_number: v }, true);
            }}
            onChange={(e) => {
              setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, extracted: { ...x.extracted, cert_number: e.target.value } } : x));
            }}
          />
        </td>
        <td className="px-3 py-2">
          <Badge variant="outline" className={statusColor[r.status] || "bg-slate-100"}>{r.status}</Badge>
          {r.extracted?.achievements && r.extracted.achievements.length > 1 && (
            <div className="mt-1 text-xs">
              <details className="cursor-pointer">
                <summary className="text-blue-700 hover:underline">
                  {r.extracted.achievements.length} achievements
                </summary>
                <table className="mt-1 border rounded bg-slate-50 text-xs">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      <th className="px-2 py-0.5 text-left font-medium">Code</th>
                      <th className="px-2 py-0.5 text-left font-medium">Category</th>
                      <th className="px-2 py-0.5 text-left font-medium">Valid</th>
                      <th className="px-2 py-0.5 text-left font-medium">Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.extracted.achievements.map((a, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-0.5 font-mono">{a.code || "—"}</td>
                        <td className="px-2 py-0.5">{a.category || "—"}</td>
                        <td className="px-2 py-0.5">{a.valid_from || "—"}</td>
                        <td className="px-2 py-0.5">{a.expiry_date || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </div>
          )}
          {r.error_msg && (
            <div className="text-red-600 text-xs mt-1 max-w-[220px]" title={r.error_msg}>{r.error_msg}</div>
          )}
        </td>
        <td className="px-3 py-2 text-right">
          <Button size="sm" variant="ghost" onClick={() => setPreviewId(r.id)} title="Preview PDF">
            <Eye className="w-4 h-4" />
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-5" data-testid="import-batch-page">
      <div className="flex items-center gap-3">
        <Link to="/imports">
          <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" />All imports</Button>
        </Link>
      </div>

      <div className="rounded-lg border bg-white p-5 shadow-sm space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" data-testid="batch-filename">{batch.filename}</h1>
            <div className="text-sm text-slate-500 mt-1">
              Uploaded {new Date(batch.uploaded_at).toLocaleString("en-GB")} by {batch.uploaded_by}
            </div>
          </div>
          <Badge variant="outline" className={`${statusColor[batch.status] || "bg-slate-100"} px-3 py-1 text-sm capitalize`} data-testid="batch-status">
            {batch.status}
          </Badge>
        </div>

        {batch.status === "failed" && batch.error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium text-red-800">This batch failed to complete</div>
                <div className="text-red-700 mt-0.5">{batch.error}</div>
                <div className="text-red-600 mt-1 text-xs">Click <b>Retry</b> above to re-run extraction with the current code — any rows that already succeeded will be kept.</div>
              </div>
            </div>
          </div>
        )}

        {batch.status === "extracting" && (
          <div>
            <div className="text-sm text-slate-600 mb-1">
              Extracting {batch.extracted}/{batch.total} ({progressPct}%) — vision OCR is reading dates &amp; cert numbers from each PDF
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 pt-1">
          {/* ── Row breakdown ─────────────────────────────────────────── */}
          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Certificate rows
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <div><span className="text-slate-500">Total: </span><b>{batch.total}</b></div>
              <div><span className="text-slate-500">Extracted: </span><b>{batch.extracted}</b></div>
              <button
                type="button"
                onClick={() => { setMatchFilter("matched"); setStatusFilter("all"); }}
                className="hover:underline text-left"
                data-testid="chip-matched"
                title="Show only rows with an existing learner + a known qualification"
              >
                <span className="text-slate-500">Matched: </span>
                <b className="text-emerald-700">{matchStats.matched}</b>
              </button>
              <button
                type="button"
                onClick={() => { setMatchFilter("new_learner"); setStatusFilter("all"); }}
                className="hover:underline text-left"
                data-testid="chip-new-learner"
                title="Rows for learners that don't yet exist — will be auto-created at commit"
              >
                <span className="text-slate-500">New learner: </span>
                <b className="text-blue-700">{matchStats.new_learner}</b>
              </button>
              <button
                type="button"
                onClick={() => { setMatchFilter("missing_qual"); setStatusFilter("all"); }}
                className="hover:underline text-left"
                data-testid="chip-missing-qual"
                title="Learner OK but qualification code was not recognised"
              >
                <span className="text-slate-500">Missing qual: </span>
                <b className="text-amber-700">{matchStats.missing_qual}</b>
              </button>
              <button
                type="button"
                onClick={() => { setMatchFilter("error"); setStatusFilter("all"); }}
                className="hover:underline text-left"
                data-testid="chip-errors"
                title="Rows that couldn't be parsed at all"
              >
                <span className="text-slate-500">Errors: </span>
                <b className="text-red-600">{batch.errored}</b>
              </button>
              {stats.superseded > 0 && (
                <button
                  type="button"
                  onClick={() => setStatusFilter("superseded")}
                  className="hover:underline text-left"
                  data-testid="chip-superseded"
                  title="Older duplicates or rows already in the database"
                >
                  <span className="text-slate-500">Superseded: </span>
                  <b className="text-amber-700">{stats.superseded}</b>
                </button>
              )}
            </div>
          </div>

          {/* ── Learner breakdown ─────────────────────────────────────── */}
          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Learners in this batch
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <div title="Existing learners referenced by ≥1 row in this batch">
                <span className="text-slate-500">Existing: </span>
                <b>{learnerStats.existingCount}</b>
                <span className="text-emerald-700 ml-1" data-testid="existing-ready">
                  ({learnerStats.existingReady} fully onboarded)
                </span>
              </div>
              <div title="Distinct new learners who don't exist yet — will be auto-created at commit">
                <span className="text-slate-500">New: </span>
                <b className="text-blue-700">{learnerStats.newCount}</b>
                <span className="text-emerald-700 ml-1" data-testid="new-ready">
                  ({learnerStats.newReady} fully onboarded)
                </span>
              </div>
              <div className="text-xs text-slate-500 basis-full pt-1">
                “Fully onboarded” = at least one cert row is ready to import
                AND no rows for that learner are errors.
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm pt-1">
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-slate-500" />
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Confidence
            </span>
          </div>
          <button
            type="button"
            onClick={() => setConfidenceFilter(confidenceFilter === "high" ? "all" : "high")}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors
              ${confidenceFilter === "high"
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"}`}
            data-testid="chip-conf-high"
            title="Vision LLM was ≥ 90% confident about the extracted dates and cert number"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            High <b className="tabular-nums">{confidenceStats.high}</b>
          </button>
          <button
            type="button"
            onClick={() => setConfidenceFilter(confidenceFilter === "medium" ? "all" : "medium")}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors
              ${confidenceFilter === "medium"
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"}`}
            data-testid="chip-conf-medium"
            title="Vision LLM was 70-89% confident — worth eyeballing before commit"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            Medium <b className="tabular-nums">{confidenceStats.medium}</b>
          </button>
          <button
            type="button"
            onClick={() => setConfidenceFilter(confidenceFilter === "low" ? "all" : "low")}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors
              ${confidenceFilter === "low"
                ? "bg-red-600 text-white border-red-600"
                : "bg-red-50 text-red-800 border-red-200 hover:bg-red-100"}`}
            data-testid="chip-conf-low"
            title="Vision LLM was < 70% confident — these need a human eyeball"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            Low <b className="tabular-nums">{confidenceStats.low}</b>
          </button>
          {confidenceStats.low > 0 && (
            <span className="text-xs text-red-700">
              ↑ these need a human eyeball before you commit
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-4 text-sm pt-1">
          {batch.default_issue_date && (
            <div><span className="text-slate-500">Default issue: </span><b>{batch.default_issue_date}</b></div>
          )}
          {batch.default_expiry_date && (
            <div><span className="text-slate-500">Default expiry: </span><b>{batch.default_expiry_date}</b></div>
          )}
          {batch.created_certs > 0 && (
            <div className="text-emerald-700">
              ✓ {batch.created_certs} cert(s) imported · +{batch.created_learners} learner(s) auto-created
            </div>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search filename, learner or qual…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
          data-testid="rows-search"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="rows-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending ({stats.pending})</SelectItem>
            <SelectItem value="approved">Approved ({stats.approved})</SelectItem>
            <SelectItem value="superseded">Superseded ({stats.superseded})</SelectItem>
            <SelectItem value="rejected">Rejected ({stats.rejected})</SelectItem>
            <SelectItem value="imported">Imported ({stats.imported})</SelectItem>
            <SelectItem value="error">Error ({stats.error})</SelectItem>
          </SelectContent>
        </Select>

        <Select value={matchFilter} onValueChange={setMatchFilter}>
          <SelectTrigger className="w-44" data-testid="rows-match-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All matches</SelectItem>
            <SelectItem value="matched">Matched ({matchStats.matched})</SelectItem>
            <SelectItem value="new_learner">New learner ({matchStats.new_learner})</SelectItem>
            <SelectItem value="missing_qual">Missing qual ({matchStats.missing_qual})</SelectItem>
            <SelectItem value="error">Errors ({matchStats.error})</SelectItem>
          </SelectContent>
        </Select>

        {(matchFilter !== "all" || statusFilter !== "all" || confidenceFilter !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setMatchFilter("all"); setStatusFilter("all"); setConfidenceFilter("all"); }}
            data-testid="clear-filters-btn"
            className="text-slate-500"
          >
            Clear filters
          </Button>
        )}

        {/* View mode toggle */}
        <div className="inline-flex rounded-md border bg-white shadow-sm">
          <button
            className={`px-3 py-1.5 text-xs flex items-center gap-1 transition-colors ${viewMode === "table" ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}
            onClick={() => setViewMode("table")}
            data-testid="view-mode-table"
            title="Flat table"
          >
            <List className="w-3.5 h-3.5" />Table
          </button>
          <button
            className={`px-3 py-1.5 text-xs flex items-center gap-1 transition-colors border-l ${viewMode === "grouped" ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}
            onClick={() => setViewMode("grouped")}
            data-testid="view-mode-grouped"
            title="Group by learner"
          >
            <Users className="w-3.5 h-3.5" />By Learner
          </button>
        </div>

        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
          <SelectTrigger className="w-28" data-testid="page-size-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25 / page</SelectItem>
            <SelectItem value="50">50 / page</SelectItem>
            <SelectItem value="100">100 / page</SelectItem>
            <SelectItem value="250">250 / page</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1" />
        <Button variant="outline" onClick={load} data-testid="reload-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
        {(batch.status === "ready" || batch.status === "done" || batch.status === "failed") && (
          <Button variant="outline" onClick={retry} data-testid="retry-btn">
            <RotateCcw className="w-4 h-4 mr-2" />Retry errored / missing rows
          </Button>
        )}
        {(batch.status === "ready" || batch.status === "done") && (
          <Button variant="outline" onClick={rerunDedup} data-testid="rerun-dedup-btn">
            <Layers className="w-4 h-4 mr-2" />Re-run dedup
          </Button>
        )}
        {readyToCommit && (
          <>
            {highConfidenceReadyIds.length > 0 && (
              <Button
                variant="outline"
                onClick={approveHighConfidence}
                data-testid="approve-high-conf-btn"
                title="Confidence ≥ 90, matched learner + qual, dates present"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Approve high-confidence ({highConfidenceReadyIds.length})
              </Button>
            )}
            <Button variant="outline" onClick={approveAllMatched} data-testid="approve-all-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />Approve all matched
            </Button>
            <Button onClick={openCommitFlow} disabled={committing || stats.pending + stats.approved === 0} data-testid="commit-btn">
              <Play className="w-4 h-4 mr-2" />{committing ? "Committing…" : "Import Approved"}
            </Button>
          </>
        )}
      </div>

      {selected.size > 0 && (
        <div className="rounded-md border bg-blue-50 px-3 py-2 flex items-center gap-2 text-sm">
          <b>{selected.size}</b> selected
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => bulkStatus("approved")} data-testid="bulk-approve-btn">Approve</Button>
          <Button size="sm" variant="outline" onClick={() => bulkStatus("rejected")}>Reject</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {viewMode === "grouped" ? (
        <div className="space-y-2">
          {groups.length === 0 ? (
            <div className="rounded-lg border bg-white p-12 text-center text-slate-500">
              No rows match this filter.
            </div>
          ) : pagedGroups.map((g) => {
            const isOpen = expandedGroups.has(g.name);
            const learnerStatus = {
              pending: g.rows.filter((r) => r.status === "pending").length,
              imported: g.rows.filter((r) => r.status === "imported").length,
              error: g.rows.filter((r) => r.status === "error").length,
            };
            return (
              <div key={g.name} className="rounded-lg border bg-white shadow-sm overflow-hidden" data-testid={`group-${g.name}`}>
                <button
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedGroups((prev) => {
                    const n = new Set(prev);
                    if (n.has(g.name)) n.delete(g.name); else n.add(g.name);
                    return n;
                  })}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <div className="flex-1">
                    <div className="font-medium">{g.name}</div>
                    <div className="text-xs text-slate-500">
                      {g.rows.length} certificate{g.rows.length === 1 ? "" : "s"} · {" "}
                      {learnerStatus.imported > 0 && <span className="text-blue-700">{learnerStatus.imported} imported · </span>}
                      {learnerStatus.pending > 0 && <span>{learnerStatus.pending} pending · </span>}
                      {learnerStatus.error > 0 && <span className="text-red-600">{learnerStatus.error} error</span>}
                    </div>
                  </div>
                  {g.rows[0]?.will_create_learner && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      <UserPlus className="w-3 h-3 mr-1" />will create
                    </Badge>
                  )}
                </button>
                {isOpen && (
                  <table className="w-full text-sm border-t">
                    <tbody>
                      {g.rows.map((r) => renderRow(r, true))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left text-slate-600">
            <tr>
              <th className="px-2 py-2 w-8">
                <Checkbox
                  checked={pageRows.length > 0 && pageRows.every((r) => selected.has(r.id))}
                  onCheckedChange={(v) => {
                    setSelected((prev) => {
                      const n = new Set(prev);
                      if (v) pageRows.forEach((r) => n.add(r.id));
                      else pageRows.forEach((r) => n.delete(r.id));
                      return n;
                    });
                  }}
                  data-testid="select-all-checkbox"
                />
              </th>
              <th className="px-3 py-2 font-medium">Filename</th>
              <th className="px-3 py-2 font-medium">Learner</th>
              <th className="px-3 py-2 font-medium">Qualification</th>
              <th className="px-3 py-2 font-medium">Issue</th>
              <th className="px-3 py-2 font-medium">Expiry</th>
              <th className="px-3 py-2 font-medium">Cert #</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">No rows match this filter.</td></tr>
            ) : pageRows.map((r) => renderRow(r, false))}
          </tbody>
        </table>
        </div>
      )}

      {/* Pagination footer */}
      {(viewMode === "table" ? pageCount : groupPageCount) > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <div>
            {viewMode === "table"
              ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filtered.length)} of ${filtered.length} rows`
              : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, groups.length)} of ${groups.length} learners`}
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
            <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Prev</Button>
            <span className="px-2 tabular-nums">
              Page <b>{page}</b> / {viewMode === "table" ? pageCount : groupPageCount}
            </span>
            <Button size="sm" variant="outline"
              onClick={() => setPage((p) => Math.min((viewMode === "table" ? pageCount : groupPageCount), p + 1))}
              disabled={page === (viewMode === "table" ? pageCount : groupPageCount)}
            >Next</Button>
            <Button size="sm" variant="outline"
              onClick={() => setPage(viewMode === "table" ? pageCount : groupPageCount)}
              disabled={page === (viewMode === "table" ? pageCount : groupPageCount)}
            >»</Button>
          </div>
        </div>
      )}

      <Dialog open={commitPreviewOpen} onOpenChange={(v) => !v && setCommitPreviewOpen(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirm import</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">This will</div>
              <div>
                Create <b>{stats.pending + stats.approved}</b> certificate(s) and&nbsp;
                auto-create <b>{learnerStats.newCount}</b> new learner(s).
              </div>
            </div>

            {newLearnerPreviewRows.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  New learners to create — edit names now to catch typos
                </div>
                <div className="max-h-[45vh] overflow-auto rounded-md border divide-y">
                  {(() => {
                    // Group new-learner rows by their current extracted name so
                    // one edit updates every row for that same person.
                    const groups = new Map();
                    for (const r of newLearnerPreviewRows) {
                      const k = (r.extracted?.learner_name || "").trim();
                      if (!k) continue;
                      if (!groups.has(k)) groups.set(k, []);
                      groups.get(k).push(r);
                    }
                    return Array.from(groups.entries()).map(([name, grpRows]) => {
                      const firstId = grpRows[0].id;
                      const editVal = previewNameEdits[firstId];
                      const displayVal = editVal !== undefined ? editVal : name;
                      return (
                        <div key={name} className="flex items-center gap-3 px-3 py-2">
                          <UserPlus className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <Input
                            value={displayVal}
                            onChange={(e) => setPreviewNameEdits((prev) => {
                              const next = { ...prev };
                              // Apply the same edit to every row in the group
                              // so they all get the corrected name at commit.
                              grpRows.forEach((r) => { next[r.id] = e.target.value; });
                              return next;
                            })}
                            data-testid={`preview-learner-name-${firstId}`}
                            className="max-w-xs"
                          />
                          <span className="text-xs text-slate-500">
                            {grpRows.length} cert{grpRows.length === 1 ? "" : "s"}
                          </span>
                          {editVal !== undefined && editVal.trim() !== name && (
                            <span className="text-xs text-amber-700">edited</span>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Tip: cross-check each name against the person you&apos;d expect —
                  a stray filename typo lands as a duplicate learner forever.
                </div>
              </div>
            )}

            {newLearnerPreviewRows.length === 0 && (
              <div className="text-slate-500 text-sm">
                No new learners will be created — every row matches an existing person.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCommitPreviewOpen(false)} disabled={savingPreviewEdits || committing}>
              Cancel
            </Button>
            <Button onClick={doCommit} disabled={savingPreviewEdits || committing} data-testid="commit-confirm-btn">
              <Play className="w-4 h-4 mr-2" />
              {savingPreviewEdits ? "Saving edits…" : committing ? "Importing…" : "Confirm & Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewId} onOpenChange={(v) => !v && setPreviewId(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>PDF preview</DialogTitle>
          </DialogHeader>
          {previewId && (
            <iframe
              src={`${process.env.REACT_APP_BACKEND_URL}/api/import/v2/rows/${previewId}/preview`}
              className="w-full h-[80vh] border-0"
              title="PDF preview"
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!commitResult} onOpenChange={(v) => !v && setCommitResult(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import complete</DialogTitle></DialogHeader>
          {commitResult && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3">
                <div><b>{commitResult.created_certs}</b> certificate(s) created</div>
                <div><b>{commitResult.created_learners}</b> learner(s) auto-created</div>
                {commitResult.skipped > 0 && <div><b>{commitResult.skipped}</b> skipped (duplicates)</div>}
              </div>
              {commitResult.errors?.length > 0 && (
                <div>
                  <div className="font-medium text-red-700 mb-1">{commitResult.errors.length} row(s) with errors:</div>
                  <ul className="text-xs text-red-600 space-y-0.5 max-h-40 overflow-auto">
                    {commitResult.errors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                  <div className="text-xs text-slate-500 mt-2">Fix these rows in the table, then click <b>Import Approved</b> again — commit is idempotent.</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCommitResult(null)}>Close</Button>
            <Button variant="outline" onClick={() => navigate("/certificates")}>View certificates</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
